import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { api, errorMessage } from "@/lib/api";
import { todayISO } from "@/lib/aba";

export const StatusDialog = ({ open, onOpenChange, action, defaultStato }) => {
  const qc = useQueryClient();
  const [stato, setStato] = useState("acquisito");
  const [data, setData] = useState(todayISO());

  useEffect(() => {
    if (!open || !action) return;
    const target = defaultStato || (action.stato === "acquisito" ? "introdotto" : "acquisito");
    setStato(target);
    setData(target === "acquisito" ? action.data_acquisizione || todayISO() : action.data_introduzione || todayISO());
  }, [open, action, defaultStato]);

  const mutation = useMutation({
    mutationFn: async () => (await api.patch(`/actions/${action.action_id}/status`, { stato, data })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action", action.action_id] });
      qc.invalidateQueries({ queryKey: ["actions", action.patient_id] });
      qc.invalidateQueries({ queryKey: ["summary", action.patient_id] });
      qc.invalidateQueries({ queryKey: ["patients"] });
      toast.success(stato === "acquisito" ? `“${action.nome}” segnata come acquisita` : `“${action.nome}” riportata a introdotta`);
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!action) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card" data-testid="status-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Stato dell'azione</DialogTitle>
          <DialogDescription>“{action.nome}” — imposta la fase e la data. Il grafico mostrerà la linea di cambio fase.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RadioGroup value={stato} onValueChange={setStato} className="grid grid-cols-2 gap-2">
            <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${stato === "introdotto" ? "border-primary bg-accent" : "border-border"}`}>
              <RadioGroupItem value="introdotto" data-testid="status-introdotto" /> <span className="text-sm font-medium">Introdotto</span>
            </label>
            <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer ${stato === "acquisito" ? "border-emerald-600 bg-emerald-50" : "border-border"}`}>
              <RadioGroupItem value="acquisito" data-testid="status-acquisito" /> <span className="text-sm font-medium">Acquisito</span>
            </label>
          </RadioGroup>
          <div className="space-y-2">
            <Label>{stato === "acquisito" ? "Data di acquisizione" : "Data di introduzione"}</Label>
            <DatePicker value={data} onChange={setData} testId="status-date-button" />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending} data-testid="status-save-button">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Salva stato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
